/**
 * "Downsizer Rules & Insights" — the eligibility and consequence panel that sits beside every
 * downsizer-contribution input, in every module.
 *
 * WHY THIS IS SHARED. It began as an inline block copy-pasted between the two Aged Care allocators,
 * and was missing entirely from Retirement Village and the Downsizer module (owner-caught,
 * 2026-08-26: "in aged care we include the hyperlink that explains the strategy and the eligibility
 * criteria — this should be here too"). A rules panel that exists in two of five places is worse
 * than none, because the client learns the rules only if they happened to enter through the right
 * door. One component, one set of words.
 *
 * THE CENTRELINK LINE IS AGE-AWARE, AND THAT IS THE POINT. The original stated flatly that the
 * contribution counts as an asset for the Age Pension and may reduce entitlements. That is
 * true only when the contributing member has REACHED Age Pension age. Below it, accumulation super
 * is exempt from BOTH means tests — verified against the engine, which returns early for
 * `superannuation` owned by a member under `PENSION_AGE` (assessableMeans.ts) and re-tests age every
 * projection year. On a real age-gap couple the difference is not academic:
 *
 *     $800k in the bank                          pension $11,114/yr   assets counted $800,000
 *     $500k into the YOUNGER (63) member's super pension $23,535/yr   assets counted $300,000
 *     $500k into the OLDER  (68) member's super  pension $11,114/yr   assets counted $800,000
 *
 * So whose name the contribution goes into can be worth more than $12,000 a year until the younger
 * member reaches Age Pension age — and the panel now says so, at the moment the adviser is choosing
 * between the two inputs.
 */
import React, { useState } from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { DOWNSIZER_CONTRIBUTION_CAP, GENERAL_TRANSFER_BALANCE_CAP, PENSION_AGE } from '../../constants/index.ts';
import { getOwnerAgeFromFactFind, downsizerExemptionPreference } from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

const ATO_URL = 'https://www.ato.gov.au/individuals/super/growing-your-super/adding-to-your-super/downsizer-contributions-for-individuals/';

/**
 * When this person reaches Age Pension age, as a month and year ("March 2030") — the date the
 * exemption ends. Said as a DATE rather than "when they turn 67" because a date is something an
 * adviser can plan a review around, and because it makes the benefit's end concrete rather than
 * abstract. `PENSION_AGE` is a flat 67, which is correct for everyone it can apply to here: anyone
 * currently under 67 was born after 1959, and 67 is the Age Pension age for every birthdate from
 * 1 January 1957 onwards.
 */
function pensionAgeDateOf(dob: string): string | null {
    const born = new Date(dob);
    if (Number.isNaN(born.getTime())) return null;
    const reaches = new Date(born.getFullYear() + PENSION_AGE, born.getMonth(), born.getDate());
    return reaches.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

const DownsizerRulesAndInsights = ({ factFind, className = '' }: { factFind: DraftFactFind; className?: string }) => {
    const [open, setOpen] = useState(false);

    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = getOwnerAgeFromFactFind(factFind as unknown as FactFind, 'client');
    const partnerAge = isCouple ? getOwnerAgeFromFactFind(factFind as unknown as FactFind, 'partner') : null;
    const clientName = factFind?.client?.firstName || 'you';
    const partnerName = factFind?.partner?.firstName || 'your partner';

    // Who, if anyone, is still under Age Pension age — the whole basis of the exemption below.
    const clientUnder = clientAge != null && clientAge < PENSION_AGE;
    const partnerUnder = partnerAge != null && partnerAge < PENSION_AGE;
    const youngerName = partnerUnder && !clientUnder ? partnerName : clientUnder && !partnerUnder ? clientName : null;
    const bothUnder = clientUnder && partnerUnder;
    const anyUnder = clientUnder || partnerUnder;

    // The allocation rule the strategy generators use, read here so the words and the split agree.
    const preference = downsizerExemptionPreference(factFind as unknown as FactFind);
    const preferredFirstName = preference.preferred === 'partner' ? partnerName : preference.preferred === 'client' ? clientName : null;
    const preferredDob = preference.preferred === 'partner' ? factFind?.partner?.dob : preference.preferred === 'client' ? factFind?.client?.dob : null;

    /*
     * THE NUDGE IS NOT BEHIND THE TAP. The rules below are reference material an adviser opens when
     * they want it; this one line is the opposite — the thing they will not think to look for, and
     * the single most valuable fact on the panel. It states WHOSE account to fill and until when,
     * using the SAME rule the strategy generators allocate by (downsizerExemptionPreference), so a
     * Suggestion Engine card can never split one way while this explains another.
     *
     * Deliberately no dollar figure here. The proposal called for one, but a separately computed
     * "worth about $X/yr" would sit inches from the Financial Impact panel's own before/after and
     * could disagree with it. The panel already prices the choice the moment the adviser makes it;
     * this says which choice to make and why.
     */
    const exemptionEnds = preferredDob ? pensionAgeDateOf(preferredDob) : null;

    return (
        <div className={`mt-2 ${className}`}>
            {preference.preferred && preferredFirstName && (
                <p className="mb-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 shadow-sm">
                    <span className="font-semibold text-slate-900">Put this in {preferredFirstName}&apos;s name.</span>{' '}
                    They are under Age Pension age ({PENSION_AGE}), so super held in their accumulation account is
                    not counted in the assets test and not deemed for the income test
                    {exemptionEnds ? <> — until <span className="font-semibold text-slate-900">{exemptionEnds}</span></> : ''}. The
                    same money in the other name is counted straight away.
                    {preference.preferredAge != null && preference.preferredAge < 60 && (
                        <> Note it also can&apos;t be drawn on for cashflow until {preferredFirstName} turns 60.</>
                    )}
                </p>
            )}
            <button type="button" className="text-blue-600 text-xs underline" onClick={() => setOpen((v) => !v)}>
                {open ? 'Hide Downsizer Rules & Insights' : 'Show Downsizer Rules & Insights'}
            </button>
            {open && (
                <div className="mt-2 space-y-2 rounded-r-lg border-l-4 border-blue-400 bg-white p-3 text-xs text-blue-900">
                    <div>
                        <strong>Eligibility:</strong> the contributing person must be <strong>55 or older</strong> at
                        the time of the contribution (there is no upper age limit and no work test). The home must
                        have been owned for at least 10 years and have been a main residence for at least part of
                        that time.
                    </div>
                    <div>
                        <strong>Contribution limit:</strong> up to{' '}
                        {formatCurrency(DOWNSIZER_CONTRIBUTION_CAP)} per person
                        {isCouple ? ` (${formatCurrency(DOWNSIZER_CONTRIBUTION_CAP * 2)} for a couple)` : ''}, and never
                        more than the actual sale proceeds. Both members of a couple can contribute from the same
                        sale, even if only one of them owned the home.
                    </div>
                    <div>
                        <strong>Timeframe:</strong> generally within <strong>90 days of settlement</strong>, and it
                        is a once-only opportunity — a downsizer contribution can only be made in relation to one
                        home sale.
                    </div>
                    <div>
                        <strong>Contribution caps:</strong> a downsizer contribution does <strong>not</strong> count
                        towards the concessional or non-concessional caps, and it can be made even when the total
                        super balance is already large. That makes it available where ordinary contributions are
                        not.
                    </div>
                    <div>
                        <strong>Income stream:</strong> the contribution can be left in accumulation, or used to
                        start an account-based pension, where earnings are tax-free. Because they are tax-free,
                        there is a lifetime limit on how much anyone can move in — the <strong>transfer balance
                        cap</strong>, currently {formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}. This tool applies it for you: anything above the remaining
                        cap stays in accumulation, taxed at 15%.
                    </div>

                    {/* The means-test consequence, told for THIS client — see the file header. */}
                    <div className="rounded border-l-2 border-blue-500 bg-white/70 px-2 py-1.5">
                        <strong>Age Pension:</strong>{' '}
                        {!anyUnder ? (
                            <>
                                because {isCouple ? 'both of you have' : `${clientName} has`} reached Age Pension age
                                ({PENSION_AGE}), money moved into super is still <strong>counted</strong> in the
                                assets test and deemed for the income test — the same as leaving it in the bank. The
                                reason to contribute here is tax and estate planning, not the pension.
                            </>
                        ) : bothUnder ? (
                            <>
                                because you are both under Age Pension age ({PENSION_AGE}), super held in{' '}
                                <strong>accumulation</strong> is exempt from the assets and income tests until you
                                reach it — so contributing can lift the pension you become entitled to, right up to
                                that point. Starting an income stream ends the exemption.
                            </>
                        ) : (
                            <>
                                <strong>whose name it goes into matters here.</strong> {youngerName} is still under
                                Age Pension age ({PENSION_AGE}), so super held in their{' '}
                                <strong>accumulation</strong> account is exempt from the assets and income tests
                                until they reach it. The same money in the older person&apos;s name is counted
                                straight away. The exemption ends the day {youngerName} reaches Age Pension age, and
                                it is lost sooner if the money is used to start an income stream.
                            </>
                        )}
                    </div>

                    <div>
                        <strong>Access:</strong> money contributed to super is generally preserved until a condition
                        of release is met, so treat it as locked away — a real trade-off if cash may be needed for
                        care or accommodation costs.
                    </div>
                    <div>
                        <a href={ATO_URL} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                            More info: ATO Downsizer Contribution
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DownsizerRulesAndInsights;
